import { Link, Navigate } from 'react-router-dom';

import { CalendarIcon, ServicesIcon, SparkIcon, StaffIcon } from '@/components/Icons';
import { StatMetric } from '@/components/StatMetric';
import { useServiceCatalogState } from '@/context/ServiceContext';
import { useSessionState } from '@/context/SessionContext';
import { useStaffDirectoryState } from '@/context/StaffContext';
import { formatCompactNumber, formatInrCurrency } from '@/lib/format';

export function ContentManagementPage() {
  const { sessionRole, isEnterprise, isReady: sessionReady } = useSessionState();
  const { isReady: servicesReady, services } = useServiceCatalogState();
  const { isReady: staffReady, staffMembers } = useStaffDirectoryState();

  if (!sessionReady || !servicesReady || !staffReady) {
    return <div className="section">Loading content management...</div>;
  }

  if (sessionRole === null) {
    return <Navigate to="/login" replace />;
  }

  if (!isEnterprise) {
    return <Navigate to="/attendance" replace />;
  }

  const serviceCount = services.length;
  const staffCount = staffMembers.length;
  const averageServiceAmount = serviceCount
    ? Math.round(services.reduce((sum, service) => sum + service.amount, 0) / serviceCount)
    : 0;
  const totalCatalogValue = services.reduce((sum, service) => sum + service.amount, 0);

  return (
    <div className="section page-grid content-management-page">
      <section className="hero-layout">
        <div className="hero-copy">
          <span className="eyebrow">
            <ServicesIcon size={14} />
            <strong>Content</strong>
            <span>Admin only</span>
          </span>

          <h1 className="display-title">
            <span>Management hub</span>
          </h1>

          <div className="button-row hero-actions">
            <Link className="primary-button" to="/content-management/services">
              <SparkIcon size={16} />
              Services
            </Link>
            <Link className="secondary-button" to="/content-management/staff">
              <StaffIcon size={16} />
              Staff
            </Link>
            <div className="icon-action-row">
              <Link className="icon-button" to="/calendar" title="Calendar" aria-label="Open calendar">
                <CalendarIcon size={18} />
              </Link>
            </div>
          </div>
        </div>

        <div className="stats-grid">
          <StatMetric label="Services" value={formatCompactNumber(serviceCount)} hint="Catalog" />
          <StatMetric label="Staff" value={formatCompactNumber(staffCount)} hint="Directory" />
          <StatMetric label="Average" value={formatInrCurrency(averageServiceAmount)} hint="Avg price" />
          <StatMetric label="Catalog" value={formatInrCurrency(totalCatalogValue)} hint="Total value" />
        </div>
      </section>

      <section className="content-grid">
        <div className="panel">
          <div className="panel-title-row">
            <div>
              <h2 className="panel-title">Top services</h2>
              <p className="panel-caption">By price</p>
            </div>
          </div>

          <div className="list">
            {services.length > 0 ? (
              services
                .slice()
                .sort((left, right) => right.amount - left.amount)
                .slice(0, 5)
                .map((service) => (
                  <div className="list-item" key={service.id}>
                    <div>
                      <strong>{service.name}</strong>
                      <span>{service.reminderDays} day reminder</span>
                    </div>
                    <strong>{formatInrCurrency(service.amount)}</strong>
                  </div>
                ))
            ) : (
              <div className="empty-state">No services yet.</div>
            )}
          </div>
        </div>

        <div className="panel">
          <div className="panel-title-row">
            <div>
              <h2 className="panel-title">Team</h2>
              <p className="panel-caption">Directory snapshot</p>
            </div>
          </div>

          <div className="list">
            {staffMembers.length > 0 ? (
              staffMembers.slice(0, 5).map((staff) => (
                <Link key={staff.id} className="list-item" to={`/staff/${staff.id}`}>
                  <div>
                    <strong>{staff.name}</strong>
                    <span>{staff.role}</span>
                  </div>
                  <span className="badge soft">{formatInrCurrency(staff.revenue)}</span>
                </Link>
              ))
            ) : (
              <div className="empty-state">No staff yet.</div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
